// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module Tracker
 * @package @geenius/analytics
 * @description Shared tracker factory that coordinates provider dispatch,
 * consent checks, and privacy sanitization for every published analytics
 * variant. Framework bindings wrap this tracker rather than reimplementing the
 * dispatch lifecycle themselves.
 */

import type { AnalyticsConfigurationBundle } from "./config";
import { AnalyticsProviderError } from "./errors";
import type { AnalyticsConfig, AnalyticsEvent, PageView } from "./index";
import {
  defaultAnalyticsConfig,
  detectDevice,
  generateSessionId,
  shouldTrack,
} from "./index";
import {
  getRetentionCutoffTimestamp,
  sanitizeAnalyticsEvent,
  sanitizePageView,
  sanitizeUserTraits,
  truncateIpAddress,
} from "./privacy";
import { GoogleAnalyticsProvider } from "./providers/google-analytics";
import type { AnalyticsProvider } from "./providers/interface";
import { MixpanelProvider } from "./providers/mixpanel";
import { PlausibleProvider } from "./providers/plausible";
import { PostHogProvider } from "./providers/posthog";
import type {
  AnalyticsStorageAdapter,
  ConsentCategory,
  ConsentSettings,
  ProviderConfig,
  UserTraits,
} from "./types";
import {
  AnalyticsEventSchema,
  PageViewSchema,
  UserTraitsSchema,
} from "./validators";

type TrackerProviderInput = AnalyticsProvider | ProviderConfig;
type TrackerProviderRegistry = Partial<
  Record<ProviderConfig["provider"], ProviderConfig>
>;
interface NormalizedProvider {
  provider: AnalyticsProvider;
  initConfig?: ProviderConfig & AnalyticsConfig;
  initialized: boolean;
}

type QueuedAnalyticsPayload =
  | { kind: "event"; payload: AnalyticsEvent }
  | { kind: "page"; payload: PageView };

export interface TrackerConfig extends AnalyticsConfig {
  providers?: TrackerProviderInput[] | TrackerProviderRegistry;
  storage?: AnalyticsStorageAdapter | AnalyticsStorageAdapter[];
}

export type TrackerConfigInput = TrackerConfig | AnalyticsConfigurationBundle;

/**
 * Options for categorizing manual events against granular consent settings.
 *
 * @property scope Consent category required before the tracker dispatches the event.
 *
 * @example
 * ```ts
 * await tracker.track("campaign_cta_clicked", {}, { scope: "marketing" })
 * ```
 */
export interface TrackerTrackOptions {
  scope?: ConsentCategory;
}

function getBrowserDocument(): Document | null {
  return typeof globalThis.document === "undefined"
    ? null
    : globalThis.document;
}

function getBrowserLocationHref(): string {
  return typeof globalThis.location === "undefined"
    ? ""
    : globalThis.location.href;
}

function getBrowserWindow(): Window | null {
  return typeof globalThis.window === "undefined" ? null : globalThis.window;
}

function getBrowserStorage(): Storage | null {
  try {
    if (typeof globalThis.localStorage === "undefined") {
      return null;
    }

    const storage = globalThis.localStorage;
    return typeof storage.getItem === "function" &&
      typeof storage.setItem === "function" &&
      typeof storage.removeItem === "function"
      ? storage
      : null;
  } catch {
    return null;
  }
}

/**
 * Public tracker contract shared by framework bindings and standalone consumers.
 *
 * @property track Dispatches a custom analytics event with optional consent scope.
 * @property page Dispatches a page-view record when tracking is enabled for the URL.
 * @property pageView Dispatches a page-view record using the canonical public method name.
 * @property identify Associates subsequent events with a known user identity.
 * @property setUserProperties Updates user traits attached to later events and provider profiles.
 * @property flush Dispatches queued track/page payloads to storage and providers.
 * @property reset Clears identity state and rolls the session identifier forward.
 * @property shutdown Flushes queued work and shuts down initialized providers.
 * @property purgeExpiredData Purges records and aggregates outside the retention window.
 * @property consent Updates the in-memory consent gate used before dispatching analytics.
 * @property deleteUserData Deletes a known user from self-hosted storage and provider integrations.
 * @property forgetUser Alias for deleteUserData, useful for right-to-forget UI copy.
 * @property startAutoTracking Installs browser listeners for configured automatic tracking.
 * @property getSessionId Returns the current session identifier used for new events.
 * @property isReady Indicates whether the tracker instance is initialized and usable.
 */
export interface TrackerInstance {
  track: (
    name: string,
    properties?: Record<string, unknown>,
    options?: TrackerTrackOptions,
  ) => Promise<void>;
  page: (url: string, properties?: Record<string, unknown>) => Promise<void>;
  pageView: (
    url: string,
    properties?: Record<string, unknown>,
  ) => Promise<void>;
  identify: (userId: string, traits?: UserTraits) => Promise<void>;
  setUserProperties: (traits: UserTraits) => Promise<void>;
  flush: () => Promise<void>;
  reset: () => Promise<void>;
  shutdown: () => Promise<void>;
  purgeExpiredData: (now?: number) => Promise<void>;
  consent: (settings: ConsentSettings) => void;
  deleteUserData: (userId: string) => Promise<void>;
  forgetUser: (userId: string) => Promise<void>;
  startAutoTracking: () => () => void;
  getSessionId: () => string;
  isReady: () => boolean;
}

/**
 * Creates the shared analytics tracker used by UI providers and standalone consumers.
 *
 * @param config Tracker configuration including optional provider instances.
 * @returns A tracker instance that coordinates analytics dispatch and consent.
 * @throws {AnalyticsProviderError} When one or more configured providers fail during dispatch.
 */
function isAnalyticsProvider(
  value: TrackerProviderInput,
): value is AnalyticsProvider {
  return (
    typeof value === "object" &&
    value !== null &&
    "trackEvent" in value &&
    "trackPageView" in value &&
    "identify" in value &&
    "setUserProperties" in value &&
    "flush" in value &&
    "reset" in value &&
    "shutdown" in value
  );
}

function isConfigurationBundle(
  value: TrackerConfigInput,
): value is AnalyticsConfigurationBundle {
  return (
    typeof value === "object" &&
    value !== null &&
    "analytics" in value &&
    "providers" in value
  );
}

function createProviderFromConfig(
  config: ProviderConfig,
  sharedConfig: AnalyticsConfig,
): NormalizedProvider {
  const provider = (() => {
    switch (config.provider) {
      case "posthog":
        return new PostHogProvider();
      case "mixpanel":
        return new MixpanelProvider();
      case "google-analytics":
        return new GoogleAnalyticsProvider();
      case "plausible":
        return new PlausibleProvider();
    }
  })();

  return {
    provider,
    initConfig: {
      ...sharedConfig,
      ...config,
    },
    initialized: false,
  };
}

function normalizeProviders(
  providerInput:
    | TrackerConfig["providers"]
    | AnalyticsConfigurationBundle["providers"]
    | undefined,
  sharedConfig: AnalyticsConfig,
): NormalizedProvider[] {
  if (!providerInput) {
    return [];
  }

  const providerEntries = Array.isArray(providerInput)
    ? providerInput
    : Object.values(providerInput);

  return providerEntries
    .filter((provider): provider is TrackerProviderInput => Boolean(provider))
    .flatMap((provider) => {
      if (isAnalyticsProvider(provider)) {
        return [{ provider, initialized: true }];
      }

      if (provider.enabled === false) {
        return [];
      }

      return [createProviderFromConfig(provider, sharedConfig)];
    });
}

function normalizeStorage(
  storage: TrackerConfig["storage"] | AnalyticsConfigurationBundle["storage"],
): AnalyticsStorageAdapter[] {
  if (!storage) return [];
  return Array.isArray(storage) ? storage : [storage];
}

function normalizeMetadataKey(key: string): string {
  return key.replace(/[-_\s]/g, "").toLowerCase();
}

function isIpMetadataKey(key: string): boolean {
  const normalizedKey = normalizeMetadataKey(key);
  return normalizedKey === "ip" || normalizedKey === "ipaddress";
}

function isPlainMetadataObject(
  value: unknown,
): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function truncateIpMetadataValue(value: unknown): unknown {
  if (typeof value === "string") {
    return truncateIpAddress(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => truncateIpMetadataValue(item));
  }

  if (isPlainMetadataObject(value)) {
    return truncateIpMetadata(value);
  }

  return value;
}

function truncateIpMetadata(
  value: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [
      key,
      isIpMetadataKey(key)
        ? truncateIpMetadataValue(entry)
        : isPlainMetadataObject(entry)
          ? truncateIpMetadata(entry)
          : Array.isArray(entry)
            ? entry.map((item) =>
                isPlainMetadataObject(item) ? truncateIpMetadata(item) : item,
              )
            : entry,
    ]),
  );
}

function truncatePayloadIpFields<
  TPayload extends { ipAddress?: string; properties?: Record<string, unknown> },
>(payload: TPayload): TPayload {
  return {
    ...payload,
    ipAddress:
      typeof payload.ipAddress === "string"
        ? truncateIpAddress(payload.ipAddress)
        : payload.ipAddress,
    properties: payload.properties
      ? truncateIpMetadata(payload.properties)
      : payload.properties,
  };
}

type HistoryStateMethod = (
  data: unknown,
  unused: string,
  url?: string | URL | null,
) => void;

function patchHistoryStateMethod(
  history: History,
  methodName: "pushState" | "replaceState",
  afterChange: () => void,
): () => void {
  const originalDescriptor = Object.getOwnPropertyDescriptor(
    history,
    methodName,
  );
  const originalMethod = history[methodName].bind(
    history,
  ) as HistoryStateMethod;
  const patchedMethod: HistoryStateMethod = (data, unused, url) => {
    originalMethod(data, unused, url);
    afterChange();
  };

  Object.defineProperty(patchedMethod, "length", {
    configurable: true,
    value: history[methodName].length,
  });
  Object.defineProperty(history, methodName, {
    configurable: true,
    writable: true,
    value: patchedMethod,
  });

  return () => {
    if (originalDescriptor) {
      Object.defineProperty(history, methodName, originalDescriptor);
      return;
    }

    delete history[methodName];
  };
}

export function createTracker(config: TrackerConfigInput): TrackerInstance {
  const normalizedInput = isConfigurationBundle(config)
    ? {
        ...config.analytics,
        providers: config.providers,
        storage: config.storage,
      }
    : config;

  const {
    providers: providerInput,
    storage: storageInput,
    ...trackerOptions
  } = normalizedInput;
  const cfg = { ...defaultAnalyticsConfig, ...trackerOptions };
  const providers = normalizeProviders(providerInput, cfg);
  const storageAdapters = normalizeStorage(storageInput);
  let sessionId = generateSessionId();
  let userId: string | undefined;
  let currentTraits: UserTraits | undefined;
  let consentSettings: ConsentSettings | null = null;
  let ready = true;
  const lastEventAt = new Map<string, number>();
  let flushTimer: ReturnType<typeof setTimeout> | null = null;
  let flushing: Promise<void> | null = null;
  let persistScheduled = false;
  const batchSize = Math.max(1, Math.floor(cfg.batchSize ?? 1));
  const flushIntervalMs = Math.max(0, Math.floor(cfg.flushIntervalMs ?? 0));
  const eventThrottleMs = Math.max(0, Math.floor(cfg.eventThrottleMs ?? 0));
  const persistOfflineQueue = cfg.persistOfflineQueue !== false;
  const offlineQueueKey =
    cfg.offlineQueueKey || "geenius:analytics:offline-queue";
  const replayOnReconnect = cfg.replayOnReconnect !== false;

  const readPersistedQueue = (): QueuedAnalyticsPayload[] => {
    if (!persistOfflineQueue) {
      return [];
    }

    const storage = getBrowserStorage();
    if (!storage) {
      return [];
    }

    try {
      const rawQueue = storage.getItem(offlineQueueKey);
      const parsed = rawQueue ? JSON.parse(rawQueue) : [];
      return Array.isArray(parsed)
        ? parsed.filter(
            (item): item is QueuedAnalyticsPayload =>
              item?.kind === "event" || item?.kind === "page",
          )
        : [];
    } catch {
      storage.removeItem(offlineQueueKey);
      return [];
    }
  };

  const queue: QueuedAnalyticsPayload[] = readPersistedQueue();

  const canTrack = (scope: ConsentCategory = "analytics"): boolean => {
    if (!ready) return false;
    if (!shouldTrack(cfg)) return false;
    if (!consentSettings) return cfg.allowPreConsentTracking === true;
    return consentSettings[scope] === true;
  };

  const normalizeConsent = (settings: ConsentSettings): ConsentSettings => {
    const personalization =
      settings.personalization ?? settings.functional ?? false;
    return {
      ...settings,
      essential: settings.essential ?? true,
      personalization,
      functional: settings.functional ?? personalization,
    };
  };

  const getProviderUserId = (id: string): string => {
    if (!cfg.maskPii && !cfg.anonymize) return id;
    const sanitized = sanitizeUserTraits(
      { userId: id },
      cfg.anonymize ? "anonymize" : "mask",
    )?.userId;
    return typeof sanitized === "string" ? sanitized : "[masked]";
  };

  const ensureProviderReady = (
    entry: NormalizedProvider,
  ): AnalyticsProvider => {
    if (!entry.initialized && entry.initConfig) {
      entry.provider.init(entry.initConfig);
      entry.initialized = true;
    }

    return entry.provider;
  };

  const dispatch = async (
    action:
      | "trackEvent"
      | "trackPageView"
      | "identify"
      | "setUserProperties"
      | "flush"
      | "reset"
      | "purgeExpiredData"
      | "deleteUserData"
      | "shutdown",
    operation: (provider: AnalyticsProvider) => Promise<void>,
    options: { initializeProviders?: boolean } = {},
  ): Promise<void> => {
    if (providers.length === 0) return;

    const initializeProviders = options.initializeProviders ?? true;
    const eligibleProviders = initializeProviders
      ? providers
      : providers.filter((provider) => provider.initialized);

    if (eligibleProviders.length === 0) return;

    const results = await Promise.allSettled(
      eligibleProviders.map(async (entry) => {
        const provider = initializeProviders
          ? ensureProviderReady(entry)
          : entry.provider;
        await operation(provider);
        return provider.name;
      }),
    );

    const failures = results.filter(
      (result): result is PromiseRejectedResult => result.status === "rejected",
    );

    const firstFailure = failures[0];

    if (firstFailure) {
      throw new AnalyticsProviderError({
        message: `One or more analytics providers failed during ${action}.`,
        cause: firstFailure.reason,
        context: {
          action,
          failures: failures.map((failure) => String(failure.reason)),
        },
      });
    }
  };

  const dispatchStorage = async (
    action:
      | "trackEvent"
      | "trackPageView"
      | "identifyUser"
      | "setUserProperties"
      | "flush"
      | "purgeExpiredData"
      | "deleteUserData"
      | "shutdown",
    operation: (storage: AnalyticsStorageAdapter) => Promise<void>,
  ): Promise<void> => {
    if (storageAdapters.length === 0) return;

    const results = await Promise.allSettled(
      storageAdapters.map(async (storage) => {
        await operation(storage);
      }),
    );

    const failures = results.filter(
      (result): result is PromiseRejectedResult => result.status === "rejected",
    );
    const firstFailure = failures[0];

    if (firstFailure) {
      throw new AnalyticsProviderError({
        message: `One or more analytics storage adapters failed during ${action}.`,
        cause: firstFailure.reason,
        context: {
          action,
          failures: failures.map((failure) => String(failure.reason)),
        },
      });
    }
  };

  const stopFlushTimer = (): void => {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
  };

  const persistQueue = (): void => {
    if (!persistOfflineQueue) {
      return;
    }

    // Cancel any pending coalesced write; this call supersedes it.
    persistScheduled = false;

    const storage = getBrowserStorage();
    if (!storage) {
      return;
    }

    try {
      if (queue.length === 0) {
        storage.removeItem(offlineQueueKey);
      } else {
        storage.setItem(offlineQueueKey, JSON.stringify(queue));
      }
    } catch {
      // Persistence is best-effort; in-memory queueing still preserves this flush.
    }
  };

  // Coalesces bursts of enqueue() calls into a single localStorage write.
  // Without this, N synchronous enqueues triggered N full JSON.stringify(queue)
  // writes — O(N²) work that blocks the main thread and exhausts the 5 MB
  // localStorage quota on long offline sessions. See audit-finding #213.
  const schedulePersistQueue = (): void => {
    if (!persistOfflineQueue || persistScheduled) {
      return;
    }
    persistScheduled = true;
    queueMicrotask(() => {
      if (!persistScheduled) {
        return;
      }
      persistQueue();
    });
  };

  const isOffline = (): boolean =>
    typeof globalThis.navigator !== "undefined" &&
    globalThis.navigator.onLine === false;

  const scheduleFlush = (): void => {
    if (
      flushIntervalMs <= 0 ||
      queue.length === 0 ||
      flushTimer ||
      isOffline()
    ) {
      return;
    }

    flushTimer = setTimeout(() => {
      flushTimer = null;
      void flush();
    }, flushIntervalMs);
  };

  const dispatchQueuedPayload = async (
    item: QueuedAnalyticsPayload,
  ): Promise<void> => {
    if (item.kind === "event") {
      await dispatchStorage("trackEvent", async (storage) => {
        await storage.trackEvent(item.payload);
      });

      await dispatch("trackEvent", async (provider) => {
        await provider.trackEvent(item.payload);
      });
      return;
    }

    await dispatchStorage("trackPageView", async (storage) => {
      await storage.trackPageView(item.payload);
    });

    await dispatch("trackPageView", async (provider) => {
      await provider.trackPageView(item.payload);
    });
  };

  const flush = async (): Promise<void> => {
    if (flushing) {
      return flushing;
    }

    flushing = (async () => {
      stopFlushTimer();

      while (queue.length > 0) {
        if (isOffline()) {
          persistQueue();
          return;
        }

        const batch = queue.splice(0, batchSize);

        for (let index = 0; index < batch.length; index += 1) {
          try {
            await dispatchQueuedPayload(batch[index]!);
          } catch (error) {
            queue.unshift(...batch.slice(index));
            persistQueue();
            throw error;
          }
        }

        persistQueue();
      }

      await dispatchStorage("flush", async (storage) => {
        if (storage.flush) {
          await storage.flush();
        }
      });

      await dispatch(
        "flush",
        async (provider) => {
          await provider.flush();
        },
        { initializeProviders: false },
      );
    })();

    try {
      await flushing;
    } finally {
      flushing = null;
      if (queue.length > 0) {
        scheduleFlush();
      }
    }
  };

  const enqueue = async (item: QueuedAnalyticsPayload): Promise<void> => {
    queue.push(item);
    // Coalesced via microtask: bursts of N synchronous enqueues collapse to
    // a single localStorage write (see audit-finding #213).
    schedulePersistQueue();

    if (isOffline()) {
      return;
    }

    if (queue.length >= batchSize) {
      await flush();
      return;
    }

    scheduleFlush();
  };

  if (replayOnReconnect) {
    const win = getBrowserWindow();
    win?.addEventListener("online", () => {
      void flush();
    });
  }

  const isThrottled = (name: string, now: number): boolean => {
    if (eventThrottleMs <= 0) {
      return false;
    }

    const lastSeenAt = lastEventAt.get(name);

    if (lastSeenAt !== undefined && now - lastSeenAt < eventThrottleMs) {
      return true;
    }

    lastEventAt.set(name, now);
    return false;
  };

  const prepareEventPayload = (event: AnalyticsEvent): AnalyticsEvent => {
    if (cfg.maskPii || cfg.anonymize) {
      return sanitizeAnalyticsEvent(
        event,
        cfg.anonymize ? "anonymize" : "mask",
      );
    }

    return cfg.maskIp ? truncatePayloadIpFields(event) : event;
  };

  const preparePageViewPayload = (pageView: PageView): PageView => {
    if (cfg.maskPii || cfg.anonymize) {
      return sanitizePageView(pageView, cfg.anonymize ? "anonymize" : "mask");
    }

    return cfg.maskIp ? truncatePayloadIpFields(pageView) : pageView;
  };

  const track = async (
    name: string,
    properties?: Record<string, unknown>,
    options: TrackerTrackOptions = {},
  ): Promise<void> => {
    if (!canTrack(options.scope ?? "analytics")) return;
    const now = Date.now();
    if (isThrottled(name, now)) return;

    const event: AnalyticsEvent = {
      id: `evt_${now}_${Math.random().toString(36).slice(2, 8)}`,
      type: "custom",
      name,
      properties,
      url: getBrowserLocationHref(),
      referrer: getBrowserDocument()?.referrer ?? "",
      sessionId,
      userId,
      traits: currentTraits,
      device: detectDevice(),
      timestamp: now,
    };

    const payload = AnalyticsEventSchema.parse(prepareEventPayload(event));

    await enqueue({ kind: "event", payload });
  };

  const page = async (
    url: string,
    properties?: Record<string, unknown>,
  ): Promise<void> => {
    if (!canTrack()) return;
    if (cfg.trackPageViews === false) return;
    if (cfg.excludePaths?.some((path) => url.includes(path))) return;

    const pageView: PageView = {
      url,
      title: getBrowserDocument()?.title ?? "",
      referrer: getBrowserDocument()?.referrer ?? "",
      properties,
      sessionId,
      userId,
      timestamp: Date.now(),
    };

    const payload = PageViewSchema.parse(preparePageViewPayload(pageView));

    await enqueue({ kind: "page", payload });
  };
  const pageView = page;

  const identify = async (id: string, traits?: UserTraits): Promise<void> => {
    if (!canTrack()) return;
    const anonymousSessionId = userId ? undefined : sessionId;
    await flush();

    const validatedInputTraits = traits
      ? UserTraitsSchema.parse(traits)
      : undefined;
    const sanitizedTraits =
      cfg.maskPii || cfg.anonymize
        ? sanitizeUserTraits(
            validatedInputTraits,
            cfg.anonymize ? "anonymize" : "mask",
          )
        : validatedInputTraits;
    const providerUserId = getProviderUserId(id);
    userId = id;
    const nextTraits: UserTraits = {
      ...currentTraits,
      ...sanitizedTraits,
    };
    currentTraits = nextTraits;

    if (anonymousSessionId) {
      const mergeEvent = AnalyticsEventSchema.parse({
        id: `evt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        type: "custom",
        name: "identity_merge",
        properties: {
          anonymousSessionId,
          userId: providerUserId,
        },
        url: getBrowserLocationHref(),
        referrer: getBrowserDocument()?.referrer ?? "",
        sessionId: anonymousSessionId,
        userId: providerUserId,
        traits: currentTraits,
        device: detectDevice(),
        timestamp: Date.now(),
      });

      await dispatchStorage("trackEvent", async (storage) => {
        await storage.trackEvent(mergeEvent);
      });

      await dispatch("trackEvent", async (provider) => {
        await provider.trackEvent(mergeEvent);
      });
    }

    await dispatchStorage("identifyUser", async (storage) => {
      if (storage.identifyUser) {
        await storage.identifyUser(providerUserId, sanitizedTraits);
      }
    });

    await dispatch("identify", async (provider) => {
      await provider.identify(providerUserId, sanitizedTraits);
    });
  };

  const setUserProperties = async (traits: UserTraits): Promise<void> => {
    if (!canTrack()) return;

    const validatedInputTraits = UserTraitsSchema.parse(traits);
    const sanitizedTraits =
      cfg.maskPii || cfg.anonymize
        ? sanitizeUserTraits(
            validatedInputTraits,
            cfg.anonymize ? "anonymize" : "mask",
          )
        : validatedInputTraits;

    const nextTraits: UserTraits = {
      ...currentTraits,
      ...sanitizedTraits,
    };
    currentTraits = nextTraits;

    if (!userId) {
      return;
    }

    await flush();
    const providerUserId = getProviderUserId(userId);

    await dispatchStorage("setUserProperties", async (storage) => {
      if (storage.setUserProperties) {
        await storage.setUserProperties(providerUserId, nextTraits);
        return;
      }

      if (storage.identifyUser) {
        await storage.identifyUser(providerUserId, nextTraits);
      }
    });

    await dispatch("setUserProperties", async (provider) => {
      await provider.setUserProperties(providerUserId, nextTraits);
    });
  };

  const reset = async (): Promise<void> => {
    await flush();
    userId = undefined;
    currentTraits = undefined;
    sessionId = generateSessionId();
    await dispatch(
      "reset",
      async (provider) => {
        await provider.reset();
      },
      { initializeProviders: false },
    );
  };

  const shutdown = async (): Promise<void> => {
    if (!ready) return;

    await flush();
    stopFlushTimer();

    await dispatchStorage("shutdown", async (storage) => {
      if (storage.shutdown) {
        await storage.shutdown();
      }
    });

    await dispatch(
      "shutdown",
      async (provider) => {
        await provider.shutdown();
      },
      { initializeProviders: false },
    );

    ready = false;
  };

  const purgeExpiredData = async (now = Date.now()): Promise<void> => {
    const cutoffTimestamp = getRetentionCutoffTimestamp(cfg.retentionDays, now);
    if (cutoffTimestamp === null) {
      return;
    }

    await flush();

    await dispatchStorage("purgeExpiredData", async (storage) => {
      if (storage.purgeExpiredData) {
        await storage.purgeExpiredData(cutoffTimestamp);
      }
    });

    await dispatch(
      "purgeExpiredData",
      async (provider) => {
        if (provider.purgeExpiredData) {
          await provider.purgeExpiredData(cutoffTimestamp);
        }
      },
      { initializeProviders: false },
    );
  };

  const consent = (settings: ConsentSettings): void => {
    consentSettings = normalizeConsent(settings);
  };

  const deleteUserData = async (id: string): Promise<void> => {
    await flush();
    const providerUserId = getProviderUserId(id);

    await dispatchStorage("deleteUserData", async (storage) => {
      if (storage.deleteUserData) {
        await storage.deleteUserData(providerUserId);
      }
    });

    await dispatch(
      "deleteUserData",
      async (provider) => {
        if (provider.deleteUserData) {
          await provider.deleteUserData(providerUserId);
        }
      },
      { initializeProviders: false },
    );

    if (userId === id) {
      userId = undefined;
      currentTraits = undefined;
      sessionId = generateSessionId();
    }
  };

  const startAutoTracking = (): (() => void) => {
    const cleanups: Array<() => void> = [];
    const doc = getBrowserDocument();
    const win = getBrowserWindow();

    if (cfg.trackSessionStart !== false) {
      void track("session_start", { source: "auto" });
    }

    if (doc && cfg.trackClicks) {
      const handleClick = (event: MouseEvent): void => {
        const target = event.target instanceof Element ? event.target : null;
        void track("click", {
          tagName: target?.tagName.toLowerCase(),
          text: target?.textContent?.trim().slice(0, 120),
          id: target?.id,
          role: target?.getAttribute("role") ?? undefined,
          href: target instanceof HTMLAnchorElement ? target.href : undefined,
        });
      };

      doc.addEventListener("click", handleClick, true);
      cleanups.push(() => doc.removeEventListener("click", handleClick, true));
    }

    if (win && cfg.trackPageViews) {
      let lastTrackedHref = win.location.href;
      const trackCurrentPage = (): void => {
        const nextHref = win.location.href;
        if (cfg.trackNavigation !== false && nextHref !== lastTrackedHref) {
          void track("navigation", {
            source: "auto",
            from: lastTrackedHref,
            to: nextHref,
          });
        }
        lastTrackedHref = nextHref;
        void page(nextHref, { source: "auto" });
      };
      trackCurrentPage();
      win.addEventListener("popstate", trackCurrentPage);
      const restorePushState = patchHistoryStateMethod(
        win.history,
        "pushState",
        trackCurrentPage,
      );
      const restoreReplaceState = patchHistoryStateMethod(
        win.history,
        "replaceState",
        trackCurrentPage,
      );
      cleanups.push(() => {
        win.removeEventListener("popstate", trackCurrentPage);
        restoreReplaceState();
        restorePushState();
      });
    }

    if (win && cfg.trackErrors) {
      const handleError = (event: ErrorEvent): void => {
        void track("browser_error", {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        });
      };

      win.addEventListener("error", handleError);
      cleanups.push(() => win.removeEventListener("error", handleError));
    }

    if (win && cfg.trackPerformance) {
      const emitPerformance = (): void => {
        const navigation = win.performance
          ?.getEntriesByType("navigation")
          .at(0) as PerformanceNavigationTiming | undefined;
        if (!navigation) return;

        void track("performance", {
          domContentLoaded: navigation.domContentLoadedEventEnd,
          load: navigation.loadEventEnd,
          responseEnd: navigation.responseEnd,
        });
      };

      if (doc?.readyState === "complete") {
        emitPerformance();
      } else {
        win.addEventListener("load", emitPerformance, { once: true });
        cleanups.push(() => win.removeEventListener("load", emitPerformance));
      }
    }

    return () => {
      for (const cleanup of cleanups.reverse()) cleanup();
    };
  };

  return {
    track,
    page,
    pageView,
    identify,
    setUserProperties,
    flush,
    reset,
    shutdown,
    purgeExpiredData,
    consent,
    deleteUserData,
    forgetUser: deleteUserData,
    startAutoTracking,
    getSessionId: () => sessionId,
    isReady: () => ready,
  };
}
